import sharedSupabase from '../config/supabase';
import { requestComparisonRecomputeForItems } from './comparisonRecomputeService';

/**
 * Shopping List Service (web)
 *
 * Data access for public.shopping_lists / shopping_list_items — the
 * pickup-vendor counterpart of purchase orders created by confirming a
 * purchase allocation. Tracks what was actually found on the trip and feeds
 * verified prices back into purchase_options / inventory_items on completion.
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

const throwIfError = (error) => {
  if (error) throw new Error(error.message);
};

const toFiniteNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const lineTotalFor = (packages, price) => {
  const qty = toFiniteNumber(packages);
  const unitPrice = toFiniteNumber(price);
  return qty !== null && unitPrice !== null
    ? Number((qty * unitPrice).toFixed(4))
    : null;
};

/**
 * Open/canceled/completed lists for a store, newest first, each with an
 * `item_count` (PostgREST embed count flattened to a number).
 */
export async function fetchShoppingLists(storeId, { status } = {}, client = sharedSupabase) {
  if (!storeId) return [];
  let query = client
    .from('shopping_lists')
    .select('*, shopping_list_items(count)')
    .eq('store_id', storeId)
    .order('created_at', { ascending: false });
  if (status) query = query.eq('status', status);

  const { data, error } = await query;
  throwIfError(error);
  return (data || []).map((row) => {
    const { shopping_list_items: countEmbed, ...list } = row;
    const itemCount = Array.isArray(countEmbed) ? countEmbed[0]?.count ?? 0 : 0;
    return { ...list, item_count: itemCount };
  });
}

/**
 * One list with its items in aisle order (sort_order, then id for stability).
 *
 * @returns {Promise<Object|null>} list row with `items`, or null if not found.
 */
export async function fetchShoppingList(listId, client = sharedSupabase) {
  if (!listId) return null;
  const { data: list, error } = await client
    .from('shopping_lists')
    .select('*')
    .eq('id', listId)
    .maybeSingle();
  throwIfError(error);
  if (!list) return null;

  const { data: items, error: itemsError } = await client
    .from('shopping_list_items')
    .select('*, inventory_item:inventory_items!inventory_item_id(category)')
    .eq('shopping_list_id', listId)
    .order('sort_order', { ascending: true })
    .order('id', { ascending: true });
  throwIfError(itemsError);

  return {
    ...list,
    items: (items || []).map((item) => ({
      ...item,
      category_snapshot: item.category_snapshot || item.inventory_item?.category || null,
    })),
  };
}

/** Human-readable creator/shopper attribution and precise item-marking times. */
export async function fetchShoppingListActivity(listId, client = sharedSupabase) {
  if (!listId) return null;
  const { data, error } = await client.rpc('shopping_list_activity', {
    p_shopping_list_id: listId,
  });
  throwIfError(error);
  return data || null;
}

/**
 * The store's inventory-category → P&L-category map. The UI only renders a
 * P&L breakdown when at least one mapping exists; an unmapped item is still
 * included under "Unmapped" once that breakdown is active.
 */
export async function fetchShoppingListPLCategories(storeId, client = sharedSupabase) {
  if (!storeId) return { byInventoryCategory: {}, hasMappings: false };
  const [categoriesResult, mappingsResult] = await Promise.all([
    client.from('pnl_categories').select('id,name').eq('store_id', storeId),
    client.from('pnl_category_mappings').select('pnl_category_id,inventory_category').eq('store_id', storeId),
  ]);
  throwIfError(categoriesResult.error);
  throwIfError(mappingsResult.error);

  const names = new Map((categoriesResult.data || []).map((row) => [String(row.id), row.name]));
  const byInventoryCategory = {};
  (mappingsResult.data || []).forEach((row) => {
    const name = names.get(String(row.pnl_category_id));
    const inventoryCategory = String(row.inventory_category || '').trim().toLowerCase();
    if (name && inventoryCategory) byInventoryCategory[inventoryCategory] = name;
  });
  return {
    byInventoryCategory,
    hasMappings: Object.keys(byInventoryCategory).length > 0,
  };
}

/**
 * Save list-level planning notes. Trip completion fields are deliberately not
 * accepted here: open-list editing and completing a trip are separate actions.
 */
export async function updateShoppingListDetails(listId, { notes } = {}, client = sharedSupabase) {
  if (!listId) throw new Error('listId is required');
  const { data, error } = await client
    .from('shopping_lists')
    .update({ notes: notes == null ? null : String(notes) })
    .eq('id', listId)
    .eq('status', 'open')
    .select()
    .single();
  throwIfError(error);
  return data;
}

/** Save receipt-style charges while preserving the legacy combined total. */
export async function updateShoppingListCharges(
  listId,
  { taxAmount, otherCharges } = {},
  client = sharedSupabase,
) {
  if (!listId) throw new Error('listId is required');
  const tax = toFiniteNumber(taxAmount);
  const other = toFiniteNumber(otherCharges);
  if (tax !== null && tax < 0) throw new Error('taxAmount must be zero or greater');
  if (other !== null && other < 0) throw new Error('otherCharges must be zero or greater');
  const { data, error } = await client
    .from('shopping_lists')
    .update({
      tax_amount: tax,
      other_charges: other,
      additional_fees: (tax ?? 0) + (other ?? 0),
    })
    .eq('id', listId)
    .eq('status', 'open')
    .select()
    .single();
  throwIfError(error);
  return data;
}

/**
 * Update what the list plans to buy. The line total is always derived from the
 * same package quantity and package price written in this request so the web
 * editor cannot leave internally contradictory money behind.
 */
export async function updateShoppingListItemPlan(
  itemId,
  { packages, estimatedPrice, notes } = {},
  client = sharedSupabase,
) {
  if (!itemId) throw new Error('itemId is required');
  const qty = toFiniteNumber(packages);
  const price = toFiniteNumber(estimatedPrice);
  if (qty !== null && qty < 0) throw new Error('packages must be zero or greater');
  if (price !== null && price < 0) throw new Error('estimatedPrice must be zero or greater');

  const patch = {
    packages: qty,
    estimated_price: price,
    estimated_line_total: lineTotalFor(qty, price),
    notes: notes == null ? null : String(notes),
  };
  const { data, error } = await client
    .from('shopping_list_items')
    .update(patch)
    .eq('id', itemId)
    .select()
    .single();
  throwIfError(error);
  return data;
}

/** Remove one planned line. Completed-list read-only behavior is enforced by the UI. */
export async function removeShoppingListItem(itemId, client = sharedSupabase) {
  if (!itemId) throw new Error('itemId is required');
  const { data, error } = await client
    .from('shopping_list_items')
    .delete()
    .eq('id', itemId)
    .select('id')
    .single();
  throwIfError(error);
  return data;
}

/**
 * Products available from the list's vendor. A row is one purchase option so
 * two materially different packs remain distinct choices in the add-item tray.
 */
export async function fetchShoppingListVendorCatalog(
  storeId,
  vendorId,
  query = '',
  client = sharedSupabase,
) {
  if (!storeId || !vendorId) return [];
  const { data, error } = await client
    .from('purchase_options')
    .select(
      'id, store_id, inventory_item_id, vendor_id, vendor_name_text, vendor_sku, vendor_description, '
      + 'purchase_unit, pack_description, package_price, is_preferred, '
      + 'item:inventory_items!inner(id, store_id, description, category, location, unit, is_active, deleted_at)',
    )
    .eq('store_id', storeId)
    .eq('vendor_id', vendorId)
    .eq('status', 'active')
    .eq('is_approved', true)
    .eq('is_hidden_from_allocation', false)
    .order('is_preferred', { ascending: false });
  throwIfError(error);

  const needle = String(query || '').trim().toLowerCase();
  return (data || [])
    .filter((row) => row.item && row.item.is_active !== false && !row.item.deleted_at)
    .filter((row) => {
      if (!needle) return true;
      return [
        row.item.description,
        row.item.category,
        row.item.location,
        row.vendor_description,
        row.vendor_sku,
      ].some((value) => String(value || '').toLowerCase().includes(needle));
    })
    .sort((a, b) => String(a.item.description || '').localeCompare(String(b.item.description || '')));
}

/** Add one catalog product to an open vendor shopping list. */
export async function addShoppingListItem(
  list,
  inventoryItem,
  option,
  { packages = 1, sortOrder = 0 } = {},
  client = sharedSupabase,
) {
  if (!list?.id || !list?.store_id) throw new Error('open shopping list is required');
  if (list.status === 'completed' || list.status === 'canceled') throw new Error('completed shopping lists are read-only');
  if (!inventoryItem?.id) throw new Error('inventory item is required');
  if (!option?.id) throw new Error('purchase option is required');
  if (list.vendor_id && String(option.vendor_id) !== String(list.vendor_id)) {
    throw new Error('purchase option must belong to the shopping list vendor');
  }

  const qty = toFiniteNumber(packages) ?? 1;
  if (qty < 0) throw new Error('packages must be zero or greater');
  const price = toFiniteNumber(option.package_price);
  const row = {
    shopping_list_id: list.id,
    store_id: list.store_id,
    inventory_item_id: inventoryItem.id,
    purchase_option_id: option.id,
    vendor_id: option.vendor_id ?? list.vendor_id ?? null,
    description: inventoryItem.description || option.vendor_description || 'Item',
    package_description: option.pack_description || option.purchase_unit || null,
    packages: qty,
    estimated_price: price,
    estimated_line_total: lineTotalFor(qty, price),
    found_status: 'pending',
    sort_order: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
  };
  const { data, error } = await client
    .from('shopping_list_items')
    .insert(row)
    .select()
    .single();
  throwIfError(error);
  return data;
}

/**
 * Partial update of trip findings on one item. Only the provided fields are
 * written; passing nothing is a no-op (returns null).
 *
 * Stays a SPARSE patch: `updateShoppingListItemPlan` writes all four of its
 * columns unconditionally, so reusing that for a partial update would null the
 * ones you did not mention. These two are deliberately different shapes.
 */
export async function markItem(
  itemId,
  { foundStatus, actualQuantity, actualPrice, actualPriceSource, notPurchasedReason, notes } = {},
  client = sharedSupabase,
) {
  if (!itemId) throw new Error('itemId is required');
  const patch = {};
  if (foundStatus !== undefined) patch.found_status = foundStatus;
  if (actualQuantity !== undefined) patch.actual_quantity = actualQuantity;
  if (actualPrice !== undefined) patch.actual_price = actualPrice;
  if (actualPriceSource !== undefined) patch.actual_price_source = actualPriceSource;
  if (notPurchasedReason !== undefined) patch.not_purchased_reason = notPurchasedReason;
  if (notes !== undefined) patch.notes = notes;
  if (Object.keys(patch).length === 0) return null;

  const { data, error } = await client
    .from('shopping_list_items')
    .update(patch)
    .eq('id', itemId)
    .select()
    .single();
  throwIfError(error);
  return data;
}

/**
 * "I bought this, as planned."
 *
 * Deliberately writes NOTHING but the outcome. Carrying the planned quantity
 * and price forward as the trip's working numbers — and stamping them
 * `planned_assumed` so they can never be mistaken for evidence — is done by the
 * `trg_shopping_line_price_provenance` trigger, in one place, so the desk and
 * the phone cannot drift about what Found means and neither has to remember to
 * record the provenance.
 *
 * The trigger is fill-only-blank, so tapping Found on a line whose price the
 * shopper already typed keeps their number.
 */
export async function markFound(itemOrId, client = sharedSupabase) {
  const itemId = typeof itemOrId === 'string' ? itemOrId : itemOrId?.id;
  if (!itemId) throw new Error('itemId is required');
  return markItem(itemId, { foundStatus: 'found' }, client);
}

/**
 * A price the shopper typed. Separate from markItem so the provenance can
 * never be forgotten at a call site — an entered price that is still labelled
 * `planned_assumed` would be refused by trip completion for no reason.
 * Clearing the price clears the provenance with it.
 */
export async function recordEnteredPrice(itemId, price, client = sharedSupabase) {
  if (!itemId) throw new Error('itemId is required');
  const value = toFiniteNumber(price);
  return markItem(itemId, {
    actualPrice: value,
    actualPriceSource: value === null ? null : 'shopper_entered',
  }, client);
}

/**
 * "I did not buy this, and here is why."
 *
 * Availability is one REASON among several, not the outcome. Writes the modern
 * spelling; the legacy `unavailable` status is still read everywhere, it is
 * just no longer produced.
 */
export async function markNotPurchased(itemId, reason = null, client = sharedSupabase) {
  if (!itemId) throw new Error('itemId is required');
  // Clearing the price/quantity is the trigger's job, for the same reason
  // defaulting them is: one owner, both surfaces.
  return markItem(itemId, {
    foundStatus: 'not_purchased',
    notPurchasedReason: reason || null,
  }, client);
}

/**
 * "I bought something else instead."
 *
 * The requested item is NOT overwritten: `inventory_item_id` /
 * `purchase_option_id` keep saying what the kitchen needed, and the substitute
 * columns say what came back in the van. One row therefore reads
 * requested -> substituted_with -> actual purchase.
 *
 * `moved_to_purchase_option_id` is deliberately untouched — that column means
 * something different and narrower ("the same item, another pack or vendor")
 * and trip completion still resolves through it.
 *
 * `scope` is the shopper's separate, explicit answer to "just this trip, or
 * from now on?". It is never inferred, and it is not the same question as
 * whether the product should become preferred.
 */
export async function recordSubstitution(
  itemId,
  {
    purchaseOptionId = null,
    inventoryItemId = null,
    description = null,
    upc = null,
    vendorSku = null,
    packDescription = null,
    scope = 'one_time',
    resolution = null,
    quantity = undefined,
    price = undefined,
    userId = null,
  } = {},
  client = sharedSupabase,
) {
  if (!itemId) throw new Error('itemId is required');
  const label = description == null ? null : String(description).trim() || null;
  if (!purchaseOptionId && !inventoryItemId && !label && !upc) {
    throw new Error('a substitute needs at least a product, a barcode or a description');
  }
  if (scope !== 'one_time' && scope !== 'ongoing') {
    throw new Error("scope must be 'one_time' or 'ongoing'");
  }

  const resolved = resolution
    || (purchaseOptionId ? 'existing_option' : inventoryItemId ? 'existing_item' : upc ? 'new_candidate' : 'unresolved');

  const patch = {
    found_status: 'substituted',
    not_purchased_reason: null,
    substitute_purchase_option_id: purchaseOptionId,
    substitute_inventory_item_id: inventoryItemId,
    substitute_description: label,
    substitute_upc: upc == null ? null : String(upc).trim() || null,
    substitute_vendor_sku: vendorSku == null ? null : String(vendorSku).trim() || null,
    substitute_pack_description: packDescription == null ? null : String(packDescription).trim() || null,
    substitute_scope: scope,
    substitute_resolution: resolved,
    substitute_decided_at: new Date().toISOString(),
    substitute_decided_by: userId || null,
  };
  if (quantity !== undefined) patch.actual_quantity = toFiniteNumber(quantity);
  if (price !== undefined) {
    const value = toFiniteNumber(price);
    patch.actual_price = value;
    patch.actual_price_source = value === null ? null : 'shopper_entered';
  }

  const { data, error } = await client
    .from('shopping_list_items')
    .update(patch)
    .eq('id', itemId)
    .select()
    .single();
  throwIfError(error);
  return data;
}

/**
 * "Use this product going forward."
 *
 * Routes an explicitly-ongoing substitution into the EXISTING related-products
 * review queue as a pending `purchasing_alternative`. Cara Cara oranges do not
 * become Navel oranges — they become a product that can legitimately answer
 * the same need, once a human confirms it.
 *
 * Deliberately a separate call, made only when the shopper chose "going
 * forward". A substitution forced by an empty shelf says nothing about what
 * this kitchen wants to buy, and inferring intent from availability is exactly
 * the automatic behaviour this must not have. Nothing is merged, nothing is
 * confirmed, and no product becomes preferred — those are all separate
 * decisions with their own controls.
 *
 * A substitute that is only a description has no catalog item to relate yet;
 * the RPC says so and the product is created by the ordinary new-item path
 * when the receipt is read.
 */
export async function suggestOngoingSubstitute(itemId, userId, client = sharedSupabase) {
  if (!itemId) throw new Error('itemId is required');
  const { data, error } = await client.rpc('suggest_trip_substitution_alternative', {
    p_shopping_list_item_id: itemId,
    ...(userId ? { p_user_id: userId } : {}),
  });
  if (error) throw new Error(error.message);
  const result = data || {};
  return {
    created: result.created === true,
    reason: result.reason ?? null,
    relationshipId: result.relationship_id ?? null,
    // Stated so no caller can mistake a proposal for a decision.
    status: result.status ?? null,
    merged: false,
    preferredChanged: false,
  };
}

/** Undo a substitution without losing the line. Returns it to unanswered. */
export async function clearSubstitution(itemId, client = sharedSupabase) {
  if (!itemId) throw new Error('itemId is required');
  const { data, error } = await client
    .from('shopping_list_items')
    .update({
      found_status: 'pending',
      substitute_purchase_option_id: null,
      substitute_inventory_item_id: null,
      substitute_description: null,
      substitute_upc: null,
      substitute_vendor_sku: null,
      substitute_pack_description: null,
      substitute_scope: null,
      substitute_resolution: null,
      substitute_decided_at: null,
      substitute_decided_by: null,
      actual_price: null,
      actual_price_source: null,
      actual_quantity: null,
    })
    .eq('id', itemId)
    .select()
    .single();
  throwIfError(error);
  return data;
}

/**
 * Records "bought a different package than planned": the row keeps its
 * original purchase_option_id (history of what was allocated) and points
 * moved_to_purchase_option_id at what was actually bought. Vendor, package
 * description, and estimated price switch to the new option.
 */
export async function moveItemToOption(itemId, newOption, client = sharedSupabase) {
  if (!itemId) throw new Error('itemId is required');
  if (!newOption?.id) throw new Error('newOption with an id is required');

  const { data, error } = await client
    .from('shopping_list_items')
    .update({
      moved_to_purchase_option_id: newOption.id,
      vendor_id: newOption.vendor_id ?? null,
      package_description: newOption.pack_description ?? newOption.purchase_unit ?? null,
      estimated_price: toFiniteNumber(newOption.package_price),
    })
    .eq('id', itemId)
    .select()
    .single();
  throwIfError(error);
  return data;
}

/**
 * Completes a shopping trip.
 *
 * The rules that decide whether what you paid becomes an option's verified
 * price used to live here, in `planTripPriceUpdates`. They now live in the
 * `complete_shopping_trip` RPC, because the phone's Shopping Guide is a plain
 * <script> that cannot import this module — and a second implementation of
 * money rules is a second implementation that drifts. Finishing a trip in the
 * aisle and finishing it at the desk now do exactly the same thing.
 *
 * See supabase/migrations/20260806120200_complete_shopping_trip_rpc.sql and its
 * .verification.md for the rules and the fixtures that prove them.
 *
 * @returns {Promise<{ updatedOptionIds: Array, skippedItemIds: Array, alreadyCompleted: boolean }>}
 */
export async function completeTrip(listId, userId, client = sharedSupabase) {
  if (!listId) throw new Error('listId is required');

  const { data, error } = await client.rpc('complete_shopping_trip', {
    p_shopping_list_id: listId,
    ...(userId ? { p_user_id: userId } : {}),
  });
  if (error) throw new Error(error.message);

  const result = data || {};
  const updatedOptionIds = Array.isArray(result.updated_option_ids) ? result.updated_option_ids : [];
  const skippedItemIds = Array.isArray(result.skipped_item_ids) ? result.skipped_item_ids : [];
  const touchedItemIds = Array.isArray(result.touched_item_ids) ? result.touched_item_ids : [];

  // The trip's found prices changed option pricing — re-resolve each touched
  // item's relationship comparison. Deliberately NOT inside the RPC: it is an
  // HTTP call to another service and has no business in a transaction. The
  // trip already stands without it.
  if (touchedItemIds.length) {
    const { data: rows } = await client
      .from('shopping_lists')
      .select('store_id')
      .eq('id', listId)
      .maybeSingle();
    const storeId = rows?.store_id ?? null;
    if (storeId) void requestComparisonRecomputeForItems(storeId, touchedItemIds, client);
  }

  return {
    updatedOptionIds,
    skippedItemIds,
    touchedItemIds,
    alreadyCompleted: result.already_completed === true,
    // Legacy keys, unchanged meaning, still read by existing callers.
    found: result.found ?? null,
    unavailable: result.unavailable ?? null,
    notTicked: result.not_ticked ?? null,
    // What the trip actually was, so the report can stop saying "skipped".
    purchased: result.purchased ?? null,
    substituted: result.substituted ?? null,
    notPurchased: result.not_purchased ?? null,
    purchaseLines: result.purchase_lines ?? null,
    pricesAssumed: result.prices_assumed ?? null,
    pricesEntered: result.prices_entered ?? null,
    reportedTotal: result.reported_total ?? null,
    tripNumber: result.trip_number ?? null,
    purchaseInvoiceId: result.purchase_invoice_id ?? null,
  };
}

/**
 * Undo a completed trip.
 *
 * Finishing now writes a purchase record, so undoing has to remove it —
 * otherwise a mis-tapped Done leaves permanent phantom spend in the store's
 * purchase history. Refused once a receipt has been reconciled against the
 * trip: at that point the purchase is backed by the vendor's own document and
 * is corrected through the invoice, not retracted by the shopper.
 */
export async function reopenTrip(listId, userId, client = sharedSupabase) {
  if (!listId) throw new Error('listId is required');
  const { data, error } = await client.rpc('reopen_shopping_trip', {
    p_shopping_list_id: listId,
    ...(userId ? { p_user_id: userId } : {}),
  });
  if (error) throw new Error(error.message);
  const result = data || {};
  return {
    reopened: result.reopened === true,
    reason: result.reason ?? null,
    removedInvoiceId: result.removed_invoice_id ?? null,
  };
}

/**
 * Release a receipt that has been waiting on this trip.
 *
 * A receipt's processing is bound to the shopping list the user connected it
 * to. One scanned before Done cannot be matched when it arrives — there is no
 * finished purchase for it to answer — so it waits at `receipt_processing`,
 * and finishing the trip is what releases it. Called after completion, and
 * best-effort: a trip is finished whether or not its receipt could be matched.
 */
export async function reconcileTripReceipt(listId, storeId, client = sharedSupabase) {
  if (!listId || !storeId) return null;
  const { data: session } = await client.auth.getSession();
  const token = session?.session?.access_token;
  const base = client.supabaseUrl || client.supabaseURL;
  if (!base) return null;
  const res = await fetch(`${base}/functions/v1/store-invoice-processor`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: client.supabaseKey,
      Authorization: `Bearer ${token || client.supabaseKey}`,
    },
    body: JSON.stringify({ action: 'reconcile_trip', store_id: storeId, shopping_list_id: listId }),
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

/**
 * The purchase record a finished trip produced, with its lines.
 *
 * A trip's purchases live in `store_invoices` / `store_invoice_line_items` —
 * the same two tables every other purchase this store makes lands in, which is
 * why End-of-Period COGS and Invoice Search can see a pickup trip without a
 * new reader. Returns null for a trip that bought nothing.
 */
export async function fetchTripPurchase(listId, client = sharedSupabase) {
  if (!listId) return null;
  const { data: invoice, error } = await client
    .from('store_invoices')
    .select('*')
    .eq('shopping_list_id', listId)
    .maybeSingle();
  throwIfError(error);
  if (!invoice) return null;

  const { data: lines, error: linesError } = await client
    .from('store_invoice_line_items')
    .select('*')
    .eq('invoice_id', invoice.id)
    .order('line_number', { ascending: true });
  throwIfError(linesError);

  const all = lines || [];
  return {
    ...invoice,
    // line_kind is what every purchases reader filters on: fees and credits are
    // the trip's charges, never product spend.
    lines: all.filter((line) => (line.line_kind ?? 'item') === 'item'),
    chargeLines: all.filter((line) => (line.line_kind ?? 'item') !== 'item'),
  };
}

/**
 * Products this store could legitimately have bought instead of `item`.
 *
 * Uses the EXISTING purchasing predicate `option_serves_line_item` — the
 * Equivalent closure plus one confirmed Alternative hop — rather than
 * re-deriving relationship traversal. That predicate is why "Restaurant Depot
 * is out of Sprite BIB" can offer the Sysco equivalent at all: the alternatives
 * live one layer up in `item_relationships`, not as extra purchase options on
 * the same item, which is why the old Move popover was always empty.
 */
export async function fetchSubstituteCandidates(storeId, inventoryItemId, client = sharedSupabase) {
  if (!storeId || !inventoryItemId) return [];
  const { data, error } = await client
    .from('purchase_options')
    .select(
      'id, store_id, inventory_item_id, vendor_id, vendor_description, vendor_sku, upc, '
      + 'purchase_unit, pack_description, package_price, qty_per_package, is_preferred, '
      + 'item:inventory_items!inner(id, description, category, unit, is_active, deleted_at), '
      + 'vendor:vendors(id, display_name)',
    )
    .eq('store_id', storeId)
    .eq('status', 'active')
    .eq('is_approved', true)
    .eq('is_hidden_from_allocation', false);
  throwIfError(error);

  const rows = (data || []).filter((row) => row.item && row.item.is_active !== false && !row.item.deleted_at);
  const uniqueItemIds = [...new Set(rows.map((row) => row.inventory_item_id).filter(Boolean))];
  if (uniqueItemIds.length === 0) return [];

  // One predicate call per candidate ITEM, not per option.
  const eligibility = await Promise.all(uniqueItemIds.map(async (candidateItemId) => {
    if (String(candidateItemId) === String(inventoryItemId)) return [candidateItemId, true];
    const { data: serves } = await client.rpc('option_serves_line_item', {
      p_store_id: storeId,
      p_option_item_id: candidateItemId,
      p_line_item_id: inventoryItemId,
    });
    return [candidateItemId, serves === true];
  }));
  const allowed = new Map(eligibility);

  return rows
    .filter((row) => allowed.get(row.inventory_item_id))
    .sort((a, b) => String(a.item?.description || '').localeCompare(String(b.item?.description || '')));
}

/**
 * Find a vendor product by the barcode on its shelf tag.
 *
 * Scoped to the store, and preferring this trip's vendor when the same barcode
 * is on file for several — a Restaurant Depot trip should resolve to the
 * Restaurant Depot product. Returns null when nothing matches, which is a
 * normal outcome and never blocks the trip.
 */
export async function findOptionByUpc(storeId, upc, { vendorId = null } = {}, client = sharedSupabase) {
  const code = String(upc ?? '').trim();
  if (!storeId || !code) return null;
  const { data, error } = await client
    .from('purchase_options')
    .select(
      'id, store_id, inventory_item_id, vendor_id, vendor_description, vendor_sku, upc, '
      + 'purchase_unit, pack_description, package_price, qty_per_package, '
      + 'item:inventory_items(id, description, category, unit), '
      + 'vendor:vendors(id, display_name)',
    )
    .eq('store_id', storeId)
    .eq('upc', code)
    .limit(10);
  throwIfError(error);
  const rows = data || [];
  if (rows.length === 0) return null;
  if (vendorId) {
    const sameVendor = rows.find((row) => String(row.vendor_id) === String(vendorId));
    if (sameVendor) return sameVendor;
  }
  return rows[0];
}

/**
 * Teach a vendor product the barcode it was scanned by.
 *
 * Fill-only-blank: a code already on the row is never overwritten from a scan,
 * for the same reason vendor fee capture is fill-only-blank — weaker evidence
 * must not displace a value somebody already established.
 */
export async function attachUpcToOption(optionId, upc, client = sharedSupabase) {
  const code = String(upc ?? '').trim();
  if (!optionId || !code) return null;
  const { data, error } = await client
    .from('purchase_options')
    .update({ upc: code })
    .eq('id', optionId)
    .is('upc', null)
    .select('id, upc')
    .maybeSingle();
  throwIfError(error);
  return data;
}
